import { useMemo, useState } from 'react'
import { useDebounce } from './useDebounce'
import { DEFAULT_PAGE_SIZE } from '@/lib/listFetch'

// Local controller for a server-paginated list: page, limit, debounced search,
// and arbitrary extra filters. Resets to page 1 whenever search/filters change.
export function useListParams<F extends Record<string, unknown>>(
  initialFilters: F = {} as F
) {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [filters, setFiltersState] = useState<F>(initialFilters)
  const debouncedSearch = useDebounce(search, 300)

  const setFilters = (next: Partial<F>) => {
    setFiltersState(prev => ({ ...prev, ...next }))
    setPage(1)
  }

  const onSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const query = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      ...filters,
    }),
    [page, limit, debouncedSearch, filters]
  )

  return {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  }
}
